import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Instagram, Facebook, MessageCircle, Mail, Phone, MapPin } from "lucide-react";

/**
 * SiteSettingsManager
 * -------------------
 * Admin panel section for editing social links & contact info shown across
 * the site (Footer, Contact page, WhatsApp FAB pre-fill).
 */
export default function SiteSettingsManager() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/site")
      .then(({ data }) => setSettings(data))
      .catch((err) => console.error("Failed to load site settings:", err));
  }, []);

  const update = (field) => (e) =>
    setSettings((s) => ({ ...s, [field]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/site", settings);
      toast.success("Site settings updated");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  const rows = [
    { key: "instagram_url", label: "Instagram URL", icon: Instagram, placeholder: "https://instagram.com/yourhandle" },
    { key: "facebook_url", label: "Facebook URL (optional)", icon: Facebook, placeholder: "https://facebook.com/yourpage" },
    { key: "whatsapp_number", label: "WhatsApp Number (with country code, no spaces)", icon: MessageCircle, placeholder: "918660515091" },
    { key: "support_email", label: "Support Email", icon: Mail, placeholder: "hello@yourdomain.com" },
    { key: "support_phone", label: "Support Phone (display)", icon: Phone, placeholder: "+91 12345 67890" },
    { key: "address", label: "Business Address", icon: MapPin, placeholder: "Street, City — Pincode" },
  ];

  return (
    <section className="mt-20" data-testid="site-settings-manager">
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="font-serif-h font-light text-3xl text-stone-900">Social & Contact</h2>
        <div className="text-xs tracking-overline uppercase text-stone-500">
          Update social links, email, phone, WhatsApp — shown in footer & contact page
        </div>
      </div>

      <div className="border border-warm bg-white p-6 rounded-sm max-w-3xl">
        {rows.map(({ key, label, icon: Icon, placeholder }) => (
          <div key={key} className="mb-5 last:mb-0">
            <label className="text-[11px] tracking-overline uppercase text-stone-500 flex items-center gap-2">
              <Icon size={13} strokeWidth={1.5} className="text-[#D4AF37]" />
              {label}
            </label>
            <input
              className="input-line"
              placeholder={placeholder}
              value={settings[key] || ""}
              onChange={update(key)}
              data-testid={`site-${key}`}
            />
          </div>
        ))}

        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-ink w-full justify-center mt-4 disabled:opacity-50"
          data-testid="site-save-btn"
        >
          <Save size={14} className="mr-2" />
          {saving ? "Saving…" : "Save Social & Contact"}
        </button>
      </div>
    </section>
  );
}
